import {
  Component,
  OnInit,
  signal,
  inject,
  OnDestroy,
  ViewChild,
  computed,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { MenuModule, Menu } from 'primeng/menu';
import { MessageService } from 'primeng/api';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { PermissionService } from '@app/core/services/permission.service';
import { Subject, takeUntil, from, of, forkJoin, Observable } from 'rxjs';
import { switchMap, map, catchError } from 'rxjs/operators';
import { toSignal } from '@angular/core/rxjs-interop';
import { BlobConverterUtil } from '@shared/utils/blob-converter.util';
import { ToastModule } from 'primeng/toast';
import {
  SearchFormComponent,
  SearchFormData,
  FormFieldConfig,
} from '@shared/components/search-form/search-form.component';
import {
  TableColumn,
  SortEvent,
} from '@shared/components/data-table/data-table.component';
import { AdvancedDataTableComponent } from '@shared/components/advanced-data-table/advanced-data-table.component';
import { StatusFilterComponent } from '@shared/components/status-filter/status-filter.component';
import { AppButtonComponent } from '@shared/components/app-button/app-button.component';
import {
  ConfirmationDialogComponent,
  ConfirmationButton,
} from '@shared/components/confirmation-dialog/confirmation-dialog.component';
import {
  InboundService,
  InboundSearchParams,
  WarehouseResultSearchParams,
} from './inbound.service';
import { WarehouseOptionsService } from '@shared/services/warehouse-options.service';
import { withPaginatedSearchFields } from '@shared/utils/paginated-field.helpers';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { BASE_PATH } from '@app/api/stock-service/variables';
import { InboundNoticeListVO } from '@app/api/stock-service/model/inboundNoticeListVO';
import { PageInfoInboundNoticeListVO } from '@app/api/stock-service/model/pageInfoInboundNoticeListVO';
import { InboundNoticeFilterDTO } from '@app/api/stock-service/model/inboundNoticeFilterDTO';
import {
  INBOUND_DEFAULT_CONFIG,
  INBOUND_MESSAGES,
  INBOUND_SEARCH_CONFIG,
  INBOUND_TABLE_CONFIG,
  INBOUND_NOTICE_TABLE_CONFIG,
  INBOUND_RESULT_TABLE_CONFIG,
  INBOUND_WARRANT_DEFAULT_COLUMNS_ORDER,
  INBOUND_WARRANT_FLEXIBLE_COLUMNS_ORDER,
  INBOUND_WARRANT_ACTION_COLUMN,
  INBOUND_NOTICE_DEFAULT_COLUMNS_ORDER,
  INBOUND_NOTICE_FLEXIBLE_COLUMNS_ORDER,
  INBOUND_NOTICE_ACTION_COLUMN,
  INBOUND_RESULT_DEFAULT_COLUMNS_ORDER,
  INBOUND_RESULT_FLEXIBLE_COLUMNS_ORDER,
  INBOUND_RESULT_ACTION_COLUMN,
  InboundType,
  InboundResultStatus,
} from './inbound.config';
import { formatNumberWithCommas } from '@shared/utils/number-formatter.util';
import { formatDateToISO } from '@shared/utils/date-formatter.util';
import { openAdvancedTableColumnSelector } from '@shared/utils/advanced-table-column-selector.util';
import { mergeOneBasedPageFromPrimeEvent } from '@shared/utils/prime-paginator-one-based.util';
import { WarehouseWarrantVO } from '@app/api/model/warehouseWarrantVO';
import { WarehouseResultVO } from '@app/api/model/warehouseResultVO';

type InboundStatus = string | number | undefined;
type StatusSeverity = 'success' | 'warning' | 'danger' | 'secondary';

@Component({
  selector: 'app-inbound',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    ButtonModule,
    TagModule,
    TooltipModule,
    ToastModule,
    MenuModule,
    SearchFormComponent,
    AdvancedDataTableComponent,
    StatusFilterComponent,
    ConfirmationDialogComponent,
    AppButtonComponent,
  ],
  providers: [MessageService],
  templateUrl: './inbound.component.html',
  styleUrl: './inbound.component.css',
})
export class InboundComponent implements OnInit, OnDestroy {
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly messageService = inject(MessageService);
  private readonly inboundService = inject(InboundService);
  private readonly http = inject(HttpClient);
  private readonly basePath = inject(BASE_PATH);
  private readonly warehouseOptionsService = inject(WarehouseOptionsService);
  private readonly translateService = inject(TranslateService);
  private readonly permissionService = inject(PermissionService);
  private readonly permissionCodes = toSignal(
    this.permissionService.userPermissions$,
    {
      initialValue: this.permissionService.getCurrentPermissions(),
    },
  );
  private readonly destroy$ = new Subject<void>();

  // Permission flags - computed from PermissionService
  // Using specific submenu 'inbound' for inventory permissions
  canRead = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction('inventory.inbound.view');
  });
  canCreate = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction('inventory.inbound.create');
  });
  canUpdate = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction('inventory.inbound.edit');
  });
  canDelete = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction('inventory.inbound.delete');
  });
  canCancel = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction('inventory.inbound.cancel');
  });
  canApprove = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction('inventory.inbound.approve');
  });

  // Search form configuration
  searchFormat = signal<FormFieldConfig[]>([]);
  warehouseResultSearchFormat = signal<FormFieldConfig[]>([]);
  inboundNoticeSearchFormat = signal<FormFieldConfig[]>([]);

  // Advanced table column configurations
  // Inbound Warrant table
  defaultColumns = computed((): TableColumn[] => {
    const fromConfig = (prop: string) =>
      INBOUND_TABLE_CONFIG.find((c) => c.prop === prop);

    const cols: TableColumn[] = [];
    for (const key of INBOUND_WARRANT_DEFAULT_COLUMNS_ORDER) {
      const col = fromConfig(key);
      if (col) {
        cols.push({
          ...col,
          label: this.translateService.instant(col.label),
        });
      }
    }

    // Conditionally add actions column at the end if permitted
    if (this.hasAnyActionPermission()) {
      const actionsCol = fromConfig(INBOUND_WARRANT_ACTION_COLUMN.ACTIONS);
      if (actionsCol) {
        cols.push({
          ...actionsCol,
          label: this.translateService.instant(actionsCol.label),
        });
      }
    }

    return cols;
  });

  flexibleColumns = computed((): TableColumn[] => {
    const fromConfig = (prop: string) =>
      INBOUND_TABLE_CONFIG.find((c) => c.prop === prop);

    const cols: TableColumn[] = [];

    for (const prop of INBOUND_WARRANT_FLEXIBLE_COLUMNS_ORDER) {
      const col = fromConfig(prop);
      if (col) {
        cols.push({
          ...col,
          label: this.translateService.instant(col.label),
        });
      }
    }

    return cols;
  });

  // Inbound Notice table
  inboundNoticeDefaultColumns = computed((): TableColumn[] => {
    const fromConfig = (prop: string) =>
      INBOUND_NOTICE_TABLE_CONFIG.find((c) => c.prop === prop);

    const cols: TableColumn[] = [];
    for (const key of INBOUND_NOTICE_DEFAULT_COLUMNS_ORDER) {
      const col = fromConfig(key);
      if (col) {
        cols.push({
          ...col,
          label: this.translateService.instant(col.label),
        });
      }
    }

    // Conditionally add actions column at the end if permitted
    if (this.hasAnyActionPermission()) {
      const actionsCol = fromConfig(INBOUND_NOTICE_ACTION_COLUMN.ACTIONS);
      if (actionsCol) {
        cols.push({
          ...actionsCol,
          label: this.translateService.instant(actionsCol.label),
        });
      }
    }

    return cols;
  });

  inboundNoticeFlexibleColumns = computed((): TableColumn[] => {
    const fromConfig = (prop: string) =>
      INBOUND_NOTICE_TABLE_CONFIG.find((c) => c.prop === prop);

    const cols: TableColumn[] = [];

    for (const prop of INBOUND_NOTICE_FLEXIBLE_COLUMNS_ORDER) {
      const col = fromConfig(prop);
      if (col) {
        cols.push({
          ...col,
          label: this.translateService.instant(col.label),
        });
      }
    }

    return cols;
  });

  // Inbound Result table
  warehouseResultDefaultColumns = computed((): TableColumn[] => {
    const fromConfig = (prop: string) =>
      INBOUND_RESULT_TABLE_CONFIG.find((c) => c.prop === prop);

    const cols: TableColumn[] = [];
    for (const key of INBOUND_RESULT_DEFAULT_COLUMNS_ORDER) {
      const col = fromConfig(key);
      if (col) {
        cols.push({
          ...col,
          label: this.translateService.instant(col.label),
        });
      }
    }

    // Conditionally add actions column at the end if permitted
    if (this.hasAnyActionPermission()) {
      const actionsCol = fromConfig(INBOUND_RESULT_ACTION_COLUMN.ACTIONS);
      if (actionsCol) {
        cols.push({
          ...actionsCol,
          label: this.translateService.instant(actionsCol.label),
        });
      }
    }

    return cols;
  });

  warehouseResultFlexibleColumns = computed((): TableColumn[] => {
    const fromConfig = (prop: string) =>
      INBOUND_RESULT_TABLE_CONFIG.find((c) => c.prop === prop);

    const cols: TableColumn[] = [];

    for (const prop of INBOUND_RESULT_FLEXIBLE_COLUMNS_ORDER) {
      const col = fromConfig(prop);
      if (col) {
        cols.push({
          ...col,
          label: this.translateService.instant(col.label),
        });
      }
    }

    return cols;
  });

  // Check if user has any action permissions (READ, CREATE, UPDATE, DELETE, CANCEL, or APPROVE)
  hasAnyActionPermission = computed(
    () =>
      this.canRead() ||
      this.canCreate() ||
      this.canUpdate() ||
      this.canDelete() ||
      this.canCancel() ||
      this.canApprove(),
  );

  // Data signals
  dataSource = signal<WarehouseWarrantVO[]>([]);
  loading = signal(false);
  totalRecords = signal(0);
  searchParams = signal({
    page: 1, // Use 1-based pagination
    size: INBOUND_DEFAULT_CONFIG.PAGE_SIZE,
  });

  // Inbound Notice Data signals
  inboundNoticeDataSource = signal<InboundNoticeListVO[]>([]);
  inboundNoticeLoading = signal(false);
  inboundNoticeTotalRecords = signal(0);
  inboundNoticeSearchParams = signal<SearchFormData | undefined>(undefined);
  inboundNoticeSortField = signal<string>('creationDate');
  inboundNoticeSortOrder = signal<1 | -1>(-1); // -1 = descending (newest first)
  inboundNoticeSearchParamsPagination = signal({
    page: 1, // Use 1-based pagination
    size: INBOUND_DEFAULT_CONFIG.PAGE_SIZE,
  });

  // Warehouse options for mapping

  // Warehouse Result Data signals
  warehouseResultDataSource = signal<WarehouseResultVO[]>([]);
  warehouseResultLoading = signal(false);
  warehouseResultTotalRecords = signal(0);
  warehouseResultSortField = signal<string>('creationDate');
  warehouseResultSortOrder = signal<1 | -1>(-1); // -1 = descending (newest first)
  warehouseResultSearchParamsPagination = signal({
    page: 1, // Use 1-based pagination
    size: INBOUND_DEFAULT_CONFIG.PAGE_SIZE,
  });

  // Sorting signals
  sortField = signal<string>('');
  sortOrder = signal<1 | -1>(1);

  // Tab management
  activeTab = signal<'inbound-warrant' | 'inbound-notice' | 'inbound-result'>(
    'inbound-notice',
  );

  // Menu for actions
  @ViewChild('inboundNoticeActionMenu') inboundNoticeActionMenu!: Menu;
  @ViewChild('inboundWarrantActionMenu') inboundWarrantActionMenu!: Menu;
  currentInboundNoticeMenuModel = signal<any[]>([]);
  selectedInboundNotice: InboundNoticeListVO | null = null;
  currentInboundWarrantMenuModel = signal<any[]>([]);
  selectedInboundWarrant: WarehouseWarrantVO | null = null;

  // References to advanced table components
  @ViewChild('advancedTableWarrant')
  advancedTableWarrant?: AdvancedDataTableComponent;
  @ViewChild('advancedTableNotice')
  advancedTableNotice?: AdvancedDataTableComponent;
  @ViewChild('advancedTableResult')
  advancedTableResult?: AdvancedDataTableComponent;

  // Cancel dialog signals
  showCancelDialog = signal(false);
  selectedInboundNoticeForCancel = signal<InboundNoticeListVO | null>(null);
  cancelDialogButtons: ConfirmationButton[] = [
    {
      label: 'inbound.dialog.actions.cancel',
      action: 'cancel',
      outlined: true,
    },
    {
      label: 'inbound.dialog.actions.confirm_cancel',
      action: 'confirm',
      severity: 'danger',
    },
  ];

  // Configuration constants (for template access)
  readonly INBOUND_DEFAULT_CONFIG = INBOUND_DEFAULT_CONFIG;
  readonly InboundType = InboundType;
  readonly InboundResultStatus = InboundResultStatus;

  // Primary button configuration base
  private readonly primaryButtonBase = {
    label: this.translateService.instant('inbound.actions.add_inbound_warrant'),
    icon: 'add',
    severity: 'info' as const,
  };

  // Computed button configuration with permissions
  primaryButton = computed(() => ({
    ...this.primaryButtonBase,
    show: this.canCreate(),
  }));

  ngOnInit(): void {
    this.initializeConfigurations();
    // Check for tab query parameter first and set tab immediately
    const tabParam = this.route.snapshot.queryParams['tab'];
    if (tabParam === 'inbound-warrant') {
      this.activeTab.set('inbound-warrant');
    } else if (tabParam === 'inbound-result') {
      this.activeTab.set('inbound-result');
    } else {
      // Default to inbound-notice
      this.activeTab.set('inbound-notice');
    }

    const currentTab = this.activeTab();
    if (currentTab === 'inbound-warrant') {
      this.loadData();
    } else if (currentTab === 'inbound-result') {
      this.loadWarehouseResultData();
    } else {
      this.loadInboundNoticeData();
    }

    this.loadOptions();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onSearch(searchData: SearchFormData): void {
    this.searchParams.set({ ...this.searchParams(), page: 1 });
    this.loadData(searchData);
  }

  onReset(): void {
    this.searchParams.set({
      page: 1,
      size: INBOUND_DEFAULT_CONFIG.PAGE_SIZE,
    });
    this.sortField.set('');
    this.sortOrder.set(1);
    this.loadData();
  }

  onPageChange(event: any): void {
    this.searchParams.set(
      mergeOneBasedPageFromPrimeEvent(this.searchParams(), event),
    );
    this.loadData();
  }

  setActiveTab(
    tab: 'inbound-warrant' | 'inbound-notice' | 'inbound-result',
  ): void {
    this.activeTab.set(tab);

    // Update URL query parameters
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { tab },
      queryParamsHandling: 'merge',
    });

    // Reset pagination when switching tabs
    if (tab === 'inbound-warrant') {
      this.searchParams.set({ ...this.searchParams(), page: 1 });
      this.loadData();
    } else if (tab === 'inbound-notice') {
      this.inboundNoticeSearchParamsPagination.set({
        ...this.inboundNoticeSearchParamsPagination(),
        page: 1,
      });
      this.loadInboundNoticeData();
    } else {
      this.warehouseResultSearchParamsPagination.set({
        ...this.warehouseResultSearchParamsPagination(),
        page: 1,
      });
      this.loadWarehouseResultData();
    }
  }

  onViewDetails(record: WarehouseWarrantVO): void {
    // Check permission before navigating
    if (!this.canRead()) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translateService.instant(
          'common.permissions.no_permission',
        ),
      });
      return;
    }

    // Navigate to warehouse warrant details view with both id and code
    this.router.navigate(['/stock/inbound/warrant-view', record.id], {
      queryParams: {
        code: record.code,
        tab: 'inbound-warrant',
      },
    });
  }

  onEditInboundNotice(record: WarehouseWarrantVO): void {
    // Check permission before navigating
    // For inbound warrant, edit action requires P_IMS.APPROVE (changing status)
    if (!this.canApprove()) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translateService.instant(
          'common.permissions.no_permission',
        ),
        detail: this.translateService.instant(
          'common.permissions.no_permission',
        ),
      });
      return;
    }

    // Navigate to warehouse notice edit page with both id and code
    this.router.navigate(['/stock/inbound/edit', record.id], {
      queryParams: {
        code: record.code,
        tab: this.activeTab(),
      },
    });
  }

  getReceivingWarrantActions(record: WarehouseWarrantVO): string[] {
    const actions: string[] = [];
    const status = record?.status;

    if (!status) {
      return actions;
    }

    // Normalize status to uppercase string for comparison
    const normalizedStatus = String(status).toUpperCase();

    // Filter actions based on status and permissions
    if (normalizedStatus === 'CREATED' || normalizedStatus === '1') {
      this.addActionIfAllowed(actions, 'confirm', this.canApprove());
      this.addActionIfAllowed(actions, 'cancel', this.canCancel());
    } else if (
      normalizedStatus === 'CONFIRMED' ||
      normalizedStatus === '2' ||
      normalizedStatus === 'PARTIALLY_RECEIVED' ||
      normalizedStatus === '6'
    ) {
      this.addActionIfAllowed(actions, 'receive', this.canUpdate());
      this.addActionIfAllowed(actions, 'cancel', this.canCancel());
    }
    // For CANCELLED or other statuses, return empty array (no actions)

    return actions;
  }

  // Check if there are any actions (excluding view) after permission filtering
  hasReceivingWarrantActions(record: WarehouseWarrantVO): boolean {
    const actions = this.getReceivingWarrantActions(record);
    return actions.length > 0;
  }

  toggleReceivingWarrantMenu(event: Event, record: WarehouseWarrantVO): void {
    event.preventDefault();
    event.stopPropagation();

    this.selectedInboundWarrant = record;
    this.currentInboundWarrantMenuModel.set(
      this.getReceivingWarrantActionsModel(record),
    );

    if (this.inboundWarrantActionMenu) {
      this.inboundWarrantActionMenu.toggle(event);
    }
  }

  private getReceivingWarrantActionsModel(record: WarehouseWarrantVO): any[] {
    const actions = this.getReceivingWarrantActions(record);

    const menuItems = actions.map((action) => {
      let label = '';
      switch (action) {
        case 'confirm':
          label = this.translateService.instant('common.actions.approve');
          break;
        case 'cancel':
          label = this.translateService.instant('common.actions.cancel');
          break;
        case 'receive':
          label = this.translateService.instant('inbound.actions.receive');
          break;
      }

      return {
        label: label,
        command: () => {
          this.onReceivingWarrantActionClick(action, record);
        },
      };
    });

    return menuItems;
  }

  private updateReceivingWarrantStatus(
    record: WarehouseWarrantVO,
    newStatus: string,
  ): void {
    if (!record.id) return;
    this.inboundService
      .updateInboundStatus(record.id, newStatus)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.messageService.add({
            severity: 'success',
            summary: this.translateService.instant('common.messages.success'),
            detail: this.translateService.instant(
              'inbound.messages.inbound_warrant_status_updated_successfully',
            ),
          });
          this.loadData();
        },
        error: (error) => {
          this.messageService.add({
            severity: 'error',
            summary: this.translateService.instant('common.messages.error'),
            detail: this.translateService.instant(
              'inbound.messages.failed_to_update_inbound_warrant_status',
            ),
          });
        },
      });
  }

  private onReceivingWarrantActionClick(
    action: string,
    record: WarehouseWarrantVO,
  ): void {
    // Check permission based on action type
    if (action === 'confirm') {
      if (!this.canApprove()) {
        this.messageService.add({
          severity: 'warn',
          summary: this.translateService.instant('common.messages.warning'),
          detail: this.translateService.instant(
            'common.permissions.no_permission',
          ),
        });
        return;
      }
    } else if (action === 'cancel') {
      if (!this.canCancel()) {
        this.messageService.add({
          severity: 'warn',
          summary: this.translateService.instant('common.messages.warning'),
          detail: this.translateService.instant(
            'common.permissions.no_permission',
          ),
        });
        return;
      }
    } else if (action === 'receive') {
      if (!this.canUpdate()) {
        this.messageService.add({
          severity: 'warn',
          summary: this.translateService.instant('common.messages.warning'),
          detail: this.translateService.instant(
            'common.permissions.no_permission',
          ),
        });
        return;
      }
    }

    switch (action) {
      case 'confirm':
        this.onConfirmReceivingWarrant(record);
        break;
      case 'cancel':
        this.onCancelReceivingWarrant(record);
        break;
      case 'receive':
        this.onReceiveReceivingWarrant(record);
        break;
      default:
        console.warn('Unknown action:', action);
    }
  }

  private onConfirmReceivingWarrant(record: WarehouseWarrantVO): void {
    this.updateReceivingWarrantStatus(record, 'CONFIRMED');
  }

  private onCancelReceivingWarrant(record: WarehouseWarrantVO): void {
    this.updateReceivingWarrantStatus(record, 'CANCELLED');
  }

  private onReceiveReceivingWarrant(record: WarehouseWarrantVO): void {
    this.router.navigate(['/stock/inbound/receive', record.id], {
      queryParams: {
        code: record.code,
        tab: this.activeTab(),
      },
    });
  }

  // Warehouse Result Methods
  onWarehouseResultSearch(searchData: SearchFormData): void {
    this.warehouseResultSearchParamsPagination.set({
      ...this.warehouseResultSearchParamsPagination(),
      page: 1,
    });
    this.loadWarehouseResultData(searchData);
  }

  onWarehouseResultReset(): void {
    this.warehouseResultSearchParamsPagination.set({
      page: 1,
      size: INBOUND_DEFAULT_CONFIG.PAGE_SIZE,
    });
    this.warehouseResultSortField.set('creationDate');
    this.warehouseResultSortOrder.set(-1);
    this.loadWarehouseResultData();
  }

  onWarehouseResultPageChange(event: any): void {
    this.warehouseResultSearchParamsPagination.set(
      mergeOneBasedPageFromPrimeEvent(
        this.warehouseResultSearchParamsPagination(),
        event,
      ),
    );
    this.loadWarehouseResultData();
  }

  onWarehouseResultSortChange(event: { field: string; order: 1 | -1 }): void {
    this.warehouseResultSortField.set(event.field);
    this.warehouseResultSortOrder.set(event.order);
    this.warehouseResultSearchParamsPagination.set({
      ...this.warehouseResultSearchParamsPagination(),
      page: 1,
    });
    this.loadWarehouseResultData();
  }

  onWarehouseResultViewDetails(record: WarehouseResultVO): void {
    // Check permission before navigating
    if (!this.canRead()) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translateService.instant(
          'common.permissions.no_permission',
        ),
        detail: this.translateService.instant(
          'common.permissions.no_permission',
        ),
      });
      return;
    }

    // Navigate to warehouse result details view with both id and code
    this.router.navigate(['/stock/inbound/result-view', record.id], {
      queryParams: { code: record.code },
    });
  }

  onAddInboundNotice(): void {
    // Check permission before navigating
    if (!this.canCreate()) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translateService.instant(
          'common.permissions.no_permission',
        ),
        detail: this.translateService.instant(
          'common.permissions.no_permission',
        ),
      });
      return;
    }

    this.router.navigate(['/stock/inbound/add'], {
      queryParams: { tab: this.activeTab() },
    });
  }

  // Inbound Notice Methods
  onInboundNoticeSearch(searchData: SearchFormData): void {
    this.inboundNoticeSearchParamsPagination.set({
      ...this.inboundNoticeSearchParamsPagination(),
      page: 1,
    });
    this.inboundNoticeSearchParams.set(searchData);
    this.loadInboundNoticeData(searchData);
  }

  onInboundNoticeReset(): void {
    this.inboundNoticeSearchParamsPagination.set({
      page: 1,
      size: INBOUND_DEFAULT_CONFIG.PAGE_SIZE,
    });
    this.inboundNoticeSearchParams.set(undefined);
    this.loadInboundNoticeData();
  }

  onInboundNoticePageChange(event: any): void {
    this.inboundNoticeSearchParamsPagination.set(
      mergeOneBasedPageFromPrimeEvent(
        this.inboundNoticeSearchParamsPagination(),
        event,
      ),
    );
    this.loadInboundNoticeData(this.inboundNoticeSearchParams());
  }

  onInboundNoticeSortChange(event: SortEvent): void {
    this.inboundNoticeSortField.set(event.field);
    this.inboundNoticeSortOrder.set(event.order);
    this.inboundNoticeSearchParamsPagination.set({
      ...this.inboundNoticeSearchParamsPagination(),
      page: 1,
    }); // Reset to first page when sorting changes
    this.loadInboundNoticeData(this.inboundNoticeSearchParams());
  }

  onInboundNoticeViewDetails(record: InboundNoticeListVO): void {
    // Check permission before navigating
    if (!this.canRead()) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translateService.instant(
          'common.permissions.no_permission',
        ),
        detail: this.translateService.instant(
          'common.permissions.no_permission',
        ),
      });
      return;
    }

    // Navigate to inbound notice details view
    this.router.navigate(['/stock/inbound/inbound-notice/view', record.id], {
      queryParams: {
        tab: 'inbound-notice',
      },
    });
  }

  onInboundNoticeUpdate(record: InboundNoticeListVO): void {
    // Check permission before navigating
    if (!this.canUpdate()) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translateService.instant(
          'common.permissions.no_permission',
        ),
        detail: this.translateService.instant(
          'common.permissions.no_permission',
        ),
      });
      return;
    }

    // Navigate to inbound notice edit page
    this.router.navigate(['/stock/inbound/inbound-notice/edit', record.id], {
      queryParams: {
        tab: 'inbound-notice',
      },
    });
  }

  onInboundNoticeCreateWarrant(record: InboundNoticeListVO): void {
    // Check permission before navigating
    if (!this.canCreate()) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translateService.instant(
          'common.permissions.no_permission',
        ),
        detail: this.translateService.instant(
          'common.permissions.no_permission',
        ),
      });
      return;
    }

    // Navigate to create inbound warrant page with notice info
    this.router.navigate(['/stock/inbound/add'], {
      queryParams: {
        tab: 'inbound-warrant',
        noticeId: record.id,
      },
    });
  }

  onSortChange(sortEvent: SortEvent): void {
    this.sortField.set(sortEvent.field);
    this.sortOrder.set(sortEvent.order);

    // Use server-side sorting for Creation Time column
    if (sortEvent.field === 'creationDate') {
      const ascending = sortEvent.order === 1; // 1 = ascending, -1 = descending
      const searchParams: SearchFormData & {
        sortBy?: string;
        ascending?: boolean;
      } = {
        sortBy: sortEvent.field,
        ascending: ascending,
      };
      this.loadData(searchParams);
    } else {
      // Apply client-side sorting for other columns
      this.sortCurrentData();
    }
  }

  private sortCurrentData(): void {
    const currentData = this.dataSource();
    const sortField = this.sortField();
    const sortOrder = this.sortOrder();

    if (!sortField || currentData.length === 0) {
      return;
    }

    const sortedData = [...currentData].sort((a, b) => {
      let aValue: any = a[sortField as keyof WarehouseWarrantVO];
      let bValue: any = b[sortField as keyof WarehouseWarrantVO];

      // Handle date sorting
      if (sortField === 'creationDate') {
        aValue = aValue ? new Date(aValue).getTime() : 0;
        bValue = bValue ? new Date(bValue).getTime() : 0;
      }

      // Handle string sorting
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      // Handle null/undefined values
      aValue ??= '';
      bValue ??= '';

      // Compare values
      if (aValue < bValue) {
        return sortOrder === 1 ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortOrder === 1 ? 1 : -1;
      }
      return 0;
    });

    this.dataSource.set(sortedData);
  }

  private loadData(
    searchParams?: SearchFormData & { sortBy?: string; ascending?: boolean },
  ): void {
    this.loading.set(true);

    // Format date range filter if provided
    const beginCreationDateISO = formatDateToISO(
      searchParams?.['beginCreationDate'],
      false,
    );
    const endCreationDateISO = formatDateToISO(
      searchParams?.['endCreationDate'],
      true,
    );

    const params: InboundSearchParams = {
      page: this.searchParams().page - 1, // Convert 1-based to 0-based for API
      size: this.searchParams().size,
      ...searchParams,
      beginCreationDate: beginCreationDateISO,
      endCreationDate: endCreationDateISO,
      // Ensure default sorting parameters are always set
      sortBy: searchParams?.sortBy || 'creationDate',
      ascending: searchParams?.ascending ?? false,
    };

    this.inboundService
      .getInboundRecords(params)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.dataSource.set(this.filterData(response.pageContent));
          this.totalRecords.set(response.totalElements || 0);
          this.loading.set(false);

          // Apply current sort order to newly loaded data
          if (this.sortField()) {
            this.sortCurrentData();
          }
        },
        error: (error) => {
          this.loading.set(false);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: INBOUND_MESSAGES.ERROR.DATA_LOAD_FAILED,
          });
        },
      });
  }

  /**
   * Filter data (no filtering needed as CLOSED status is removed)
   */
  private filterData(data?: WarehouseWarrantVO[]): WarehouseWarrantVO[] {
    if (!data) return [];
    return data;
  }

  private loadOptions(): void {
    // Load inbound warrant (phiếu nhập kho) status options with translations
    this.inboundService
      .getInboundWarrantStatusOptions()
      .pipe(takeUntil(this.destroy$))
      .subscribe((options) => {
        const translatedOptions = options.map((option) => ({
          ...option,
          label: this.translateService.instant(option.label),
        }));

        // Update search format for inbound warrant (phiếu nhập kho)
        this.searchFormat.set(
          this.searchFormat().map((field) =>
            field.key === 'status'
              ? { ...field, options: translatedOptions }
              : field,
          ),
        );
      });

    // Load inbound notice (thông báo) status options with translations
    this.inboundService
      .getInboundNoticeStatusOptions()
      .pipe(takeUntil(this.destroy$))
      .subscribe((options) => {
        const translatedOptions = options.map((option) => ({
          ...option,
          label: this.translateService.instant(
            `inbound.status.${option.value.toLowerCase()}`,
          ),
        }));

        // Update inbound notice search format
        this.inboundNoticeSearchFormat.set(
          this.inboundNoticeSearchFormat().map((field) =>
            field.key === 'status'
              ? { ...field, options: translatedOptions }
              : field,
          ),
        );

        // Also update inbound notice search format
        this.inboundNoticeSearchFormat.set(
          this.inboundNoticeSearchFormat().map((field) =>
            field.key === 'status'
              ? { ...field, options: translatedOptions }
              : field,
          ),
        );
      });
  }

  private loadInboundNoticeData(searchParams?: SearchFormData): void {
    this.inboundNoticeLoading.set(true);

    // Convert search params to API format
    // Map status to StatusEnum if provided
    let statusEnum: InboundNoticeFilterDTO.StatusEnum | undefined = undefined;
    if (searchParams?.['status']) {
      const statusValue = searchParams['status'];
      // If it's already a string enum, use it directly
      if (
        typeof statusValue === 'string' &&
        Object.values(InboundNoticeFilterDTO.StatusEnum).includes(
          statusValue as InboundNoticeFilterDTO.StatusEnum,
        )
      ) {
        statusEnum = statusValue as InboundNoticeFilterDTO.StatusEnum;
      }
    }

    // Format dates to ISO string using helper
    const startDateISO = formatDateToISO(searchParams?.['fromDate'], false);
    const endDateISO = formatDateToISO(searchParams?.['toDate'], true);

    const filter: InboundNoticeFilterDTO = {
      code: searchParams?.['code'] || undefined,
      realNoticeCode: searchParams?.['realNoticeCode'] || undefined,
      noticeType: searchParams?.['noticeType'] as
        | InboundNoticeFilterDTO.NoticeTypeEnum
        | undefined,
      sourceService: searchParams?.['sourceService'] || undefined,
      sourceCode: searchParams?.['innerOrderNo'] || undefined, // sourceCode maps to innerOrderNo (PO code)
      vendorCode: searchParams?.['vendorCode'] || undefined,
      warehouseCode: searchParams?.['warehouseCode'] || undefined,
      status: statusEnum,
      startDate: startDateISO,
      endDate: endDateISO,
      page: this.inboundNoticeSearchParamsPagination().page - 1, // Convert 1-based to 0-based for API
      size: this.inboundNoticeSearchParamsPagination().size,
      sortBy: this.inboundNoticeSortField() || 'creationDate',
      ascending: this.inboundNoticeSortOrder() === 1, // 1 = ascending, -1 = descending
    };

    const url = `${this.basePath}/v1/inbound-notice/page`;
    this.http
      .post<PageInfoInboundNoticeListVO>(url, filter, {
        headers: new HttpHeaders({
          'Content-Type': 'application/json',
          Accept: 'application/json',
        }),
        observe: 'body',
        responseType: 'json',
      })
      .pipe(
        switchMap((response: any) => {
          if (response instanceof Blob) {
            return from(response.text().then((text) => JSON.parse(text)));
          }
          return of(response);
        }),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: (response: PageInfoInboundNoticeListVO) => {
          const notices = response.pageContent || [];
          this.prefetchWarehouseCodes(
            notices.map((n) => n.warehouseCode || '').filter(Boolean),
          ).subscribe(() => {
            const enrichedData = notices.map((notice) => ({
              ...notice,
              warehouseName: this.resolveWarehouseDisplayName(
                notice.warehouseCode || '',
                notice.warehouseName,
              ),
            }));
            this.inboundNoticeDataSource.set(enrichedData);
            this.inboundNoticeTotalRecords.set(response.totalElements || 0);
            this.inboundNoticeLoading.set(false);
          });
        },
        error: () => {
          this.inboundNoticeLoading.set(false);
          this.messageService.add({
            severity: 'error',
            summary: this.translateService.instant('common.messages.error'),
            detail: INBOUND_MESSAGES.ERROR.DATA_LOAD_FAILED,
          });
        },
      });
  }

  private resolveWarehouseDisplayName(
    code: string,
    fallback?: string,
  ): string {
    const cached = this.warehouseOptionsService.getWarehouseByCode(code);
    if (cached?.name) {
      return cached.name;
    }
    return fallback || code || '';
  }

  private prefetchWarehouseCodes(codes: string[]): Observable<void> {
    const unique = [...new Set(codes.filter(Boolean))].filter(
      (code) => !this.warehouseOptionsService.getWarehouseByCode(code),
    );
    if (unique.length === 0) {
      return of(undefined);
    }
    return forkJoin(
      unique.map((code) =>
        this.warehouseOptionsService
          .getOptionsPage({ page: 1, size: 1, searchText: code })
          .pipe(catchError(() => of(null))),
      ),
    ).pipe(map(() => undefined));
  }

  private loadWarehouseResultData(searchParams?: SearchFormData): void {
    this.warehouseResultLoading.set(true);

    const params: WarehouseResultSearchParams = {
      page: this.warehouseResultSearchParamsPagination().page - 1, // Convert 1-based to 0-based for API
      size: this.warehouseResultSearchParamsPagination().size,
      ...searchParams,
      // Use sorting from signals if not provided in searchParams
      sortBy: searchParams?.['sortBy'] || this.warehouseResultSortField(),
      ascending:
        searchParams?.['ascending'] ?? this.warehouseResultSortOrder() === 1,
    };

    this.inboundService
      .getWarehouseResultRecords(params)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.warehouseResultDataSource.set(response.pageContent || []);
          this.warehouseResultTotalRecords.set(response.totalElements || 0);
          this.warehouseResultLoading.set(false);
        },
        error: (error) => {
          this.warehouseResultLoading.set(false);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: INBOUND_MESSAGES.ERROR.INBOUND_RESULT_LOAD_FAILED,
          });
        },
      });
  }

  // Helper methods for displaying inbound type and status labels
  getInboundTypeLabel(inputType: string): string {
    const typeMap: { [key: string]: string } = {
      PO: this.translateService.instant('inbound.types.purchase_order'),
      TO: this.translateService.instant('inbound.types.transfer_order'),
      RO: this.translateService.instant('inbound.types.return_order'),
    };
    return typeMap[inputType] || inputType; // Fallback to raw value if not found
  }

  getInboundStatusLabel(status: InboundStatus): string {
    if (status === undefined || status === null) return '';
    // Handle numeric status (1, 2, 3, etc.)
    const statusNum =
      typeof status === 'string' ? Number.parseInt(status) : status;
    if (!Number.isNaN(statusNum)) {
      const statusMap: { [key: number]: string } = {
        1: this.translateService.instant('inbound.status.created'),
        2: this.translateService.instant('inbound.status.confirmed'),
        3: this.translateService.instant('inbound.status.in_transit'),
        4: this.translateService.instant('inbound.status.arrived'),
        5: this.translateService.instant('inbound.status.receiving'),
        6: this.translateService.instant('inbound.status.partially_received'),
        7: this.translateService.instant('inbound.status.received'),
        8: this.translateService.instant('inbound.status.closed'),
        9: this.translateService.instant('inbound.status.cancelled'),
      };
      return statusMap[statusNum] || status.toString();
    }

    // Handle string status
    const statusMap: { [key: string]: string } = {
      CREATED: this.translateService.instant('inbound.status.created'),
      CONFIRMED: this.translateService.instant('inbound.status.confirmed'),
      IN_TRANSIT: this.translateService.instant('inbound.status.in_transit'),
      ARRIVED: this.translateService.instant('inbound.status.arrived'),
      RECEIVING: this.translateService.instant('inbound.status.receiving'),
      PARTIALLY_RECEIVED: this.translateService.instant(
        'inbound.status.partially_received',
      ),
      RECEIVED: this.translateService.instant('inbound.status.received'),
      CLOSED: this.translateService.instant('inbound.status.closed'),
      CANCELLED: this.translateService.instant('inbound.status.cancelled'),
      PENDING: this.translateService.instant('inbound.status.pending'),
      INSPECTED: this.translateService.instant('inbound.status.inspected'),
      COMPLETED: this.translateService.instant('inbound.status.completed'),
    };
    return statusMap[status] || status.toString(); // Fallback to raw value if not found
  }

  getStatusSeverity(status: InboundStatus): StatusSeverity {
    if (status === undefined || status === null) return 'secondary';
    // Handle numeric status (1, 2, 3, etc.)
    const statusNum =
      typeof status === 'string' ? Number.parseInt(status) : status;
    if (!Number.isNaN(statusNum)) {
      const statusSeverity: {
        [key: number]: StatusSeverity;
      } = {
        1: 'secondary', // CREATED
        2: 'secondary', // CONFIRMED
        3: 'warning', // IN_TRANSIT
        4: 'warning', // ARRIVED
        5: 'warning', // RECEIVING
        6: 'warning', // PARTIALLY_RECEIVED
        7: 'success', // RECEIVED
        8: 'success', // CLOSED
        9: 'danger', // CANCELLED
      };
      return statusSeverity[statusNum] || 'secondary';
    }

    // Handle string status
    const statusSeverity: {
      [key: string]: StatusSeverity;
    } = {
      // Inbound Notice Status
      CREATED: 'secondary',
      CONFIRMED: 'secondary',
      IN_TRANSIT: 'warning',
      ARRIVED: 'warning',
      RECEIVING: 'warning',
      PARTIALLY_RECEIVED: 'warning',
      RECEIVED: 'success',
      CLOSED: 'success',
      CANCELLED: 'danger',
      PENDING: 'warning',
      INSPECTED: 'secondary',
      COMPLETED: 'success',
    };

    return statusSeverity[status] || 'secondary';
  }

  getStatusStyle(status: InboundStatus): any {
    const severity = this.getStatusSeverity(status);

    switch (severity) {
      case 'success':
        return {
          'background-color': '#ecfdf5',
          color: '#047857',
          border: 'none',
        };
      case 'warning':
        return {
          'background-color': '#fef3c7',
          color: '#a16207',
          border: 'none',
        };
      case 'danger':
        return {
          'background-color': '#fef2f2',
          color: '#dc2626',
          border: 'none',
        };
      case 'secondary':
      default:
        return {
          'background-color': '#f9fafb',
          color: '#6b7280',
          border: 'none',
        };
    }
  }

  // Get available actions for an inbound notice (filtered by permissions)
  getInboundNoticeActions(notice: InboundNoticeListVO): string[] {
    // Only show actions menu if status is PENDING
    if (notice.status === InboundNoticeListVO.StatusEnum.Pending) {
      const actions: string[] = [];
      // Add cancel action if user has cancel permission
      if (this.canCancel()) {
        actions.push('cancel');
      }
      // Add edit action if user has update permission
      if (this.canUpdate()) {
        actions.push('edit');
      }
      // Add create warrant action if user has create permission
      if (this.canCreate()) {
        actions.push('create_warrant');
      }
      return actions;
    }
    // For other statuses, only show view (no menu)
    return [];
  }

  // Check if the "More" button should be visible (based on original logic - status must be PENDING)
  // Toggle menu for inbound notice actions
  toggleInboundNoticeMenu(event: Event, notice: InboundNoticeListVO): void {
    this.selectedInboundNotice = notice;
    const actions = this.getInboundNoticeActions(notice);

    // Create menu items based on available actions with permission checks
    const menuItems = actions.map((action) => {
      let disabled = false;
      // Check permissions for each action
      if (action === 'cancel') {
        disabled = !this.canCancel();
      } else if (action === 'edit') {
        disabled = !this.canUpdate();
      } else if (action === 'create_warrant') {
        disabled = !this.canCreate();
      }

      return {
        label: this.translateService.instant(`common.actions.${action}`),
        command: disabled
          ? undefined
          : () => this.onInboundNoticeActionClick(action, notice),
        disabled: disabled,
      };
    });

    this.currentInboundNoticeMenuModel.set(menuItems);

    // Show menu at cursor position
    this.inboundNoticeActionMenu.toggle(event);
  }

  // Handle inbound notice action clicks
  onInboundNoticeActionClick(
    action: string,
    notice: InboundNoticeListVO,
  ): void {
    switch (action) {
      case 'cancel':
        this.onInboundNoticeCancel(notice);
        break;
      case 'edit':
        this.onInboundNoticeUpdate(notice);
        break;
      case 'create_warrant':
        this.onInboundNoticeCreateWarrant(notice);
        break;
    }
  }

  /**
   * Show cancel confirmation dialog for inbound notice
   */
  onInboundNoticeCancel(notice: InboundNoticeListVO): void {
    if (!this.canCancel()) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translateService.instant('common.messages.warning'),
        detail: this.translateService.instant(
          'common.permissions.no_permission',
        ),
      });
      return;
    }
    this.selectedInboundNoticeForCancel.set(notice);
    this.showCancelDialog.set(true);
  }

  /**
   * Handle cancel confirmation dialog button click
   * @param action - 'confirm' to cancel the notice, 'cancel' to close dialog
   */
  onCancelConfirm(action: string): void {
    if (action === 'confirm') {
      const notice = this.selectedInboundNoticeForCancel();
      if (notice?.id) {
        // PUT /v1/inbound-notice/{id}/status?status=CANCELLED
        const url = `${this.basePath}/v1/inbound-notice/${notice.id}/status?status=CANCELLED`;
        this.http
          .put(
            url,
            {},
            {
              headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
            },
          )
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: () => {
              this.messageService.add({
                severity: 'success',
                summary: this.translateService.instant(
                  'common.messages.success',
                ),
                detail: this.translateService.instant(
                  'inbound.messages.cancelled_successfully',
                ),
              });
              this.loadInboundNoticeData();
              this.showCancelDialog.set(false);
              this.selectedInboundNoticeForCancel.set(null);
            },
            error: (error) => {
              this.messageService.add({
                severity: 'error',
                summary: this.translateService.instant('common.messages.error'),
                detail: this.translateService.instant(
                  'inbound.messages.failed_to_cancel',
                ),
              });
            },
          });
      }
    } else if (action === 'cancel') {
      // Close dialog when cancel button is clicked
      this.showCancelDialog.set(false);
      this.selectedInboundNoticeForCancel.set(null);
    }
  }

  formatCreationTime(dateString: string): string {
    if (!dateString) return '-';

    try {
      const date = new Date(dateString);
      if (Number.isNaN(date.getTime())) return dateString;

      const time = date.toLocaleTimeString('vi-VN', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      });
      const datePart = date.toLocaleDateString('vi-VN', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });

      return `${time} ${datePart}`; // e.g., 16:49 09/12/2025
    } catch {
      return dateString;
    }
  }

  formatDateOnly(dateString: string | undefined): string {
    if (!dateString) return '-';

    try {
      const date = new Date(dateString);
      if (Number.isNaN(date.getTime())) return dateString;

      return date.toLocaleDateString('vi-VN', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      }); // e.g., 16/12/2025
    } catch {
      return dateString;
    }
  }

  formatQuantity(quantity: number | null | undefined): string {
    return formatNumberWithCommas(quantity);
  }

  private buildInboundNoticeSearchFormat(): FormFieldConfig[] {
    const base: FormFieldConfig[] = [
      {
        key: 'code',
        label: 'inbound.table.columns.notice_code',
        type: 'text',
        placeholder: 'inbound.search.notice_code_placeholder',
        width: '180px',
      },
      {
        key: 'innerOrderNo',
        label: 'inbound.table.columns.internal_order_number',
        type: 'text',
        placeholder: 'inbound.search.inner_order_number_placeholder',
      },
      {
        key: 'warehouseCode',
        label: 'inbound.table.columns.warehouse_code',
        type: 'searchableDropdown',
        placeholder: 'inbound.search.warehouse_code_placeholder',
        showClear: true,
        width: '200px',
      },
      {
        key: 'status',
        label: 'inbound.table.columns.status',
        type: 'select',
        placeholder: 'inbound.placeholders.select_status',
        options: [],
      },
      {
        key: 'creationTime',
        label: 'inbound.table.columns.creation_time',
        type: 'dateRange',
        range: { startKey: 'fromDate', endKey: 'toDate' },
      },
    ];

    const translated = base.map((field) => ({
      ...field,
      label: this.translateService.instant(field.label),
      placeholder: field.placeholder
        ? this.translateService.instant(field.placeholder)
        : field.placeholder,
    }));

    return withPaginatedSearchFields(translated, {
      warehouseCode: {
        loadPage: (params) =>
          this.warehouseOptionsService.getOptionsPage(params, {
            valueKey: 'code',
          }),
      },
    });
  }

  private initializeConfigurations(): void {
    // Initialize search form configurations with translations from config
    this.searchFormat.set(
      INBOUND_SEARCH_CONFIG.map((field) => ({
        ...field,
        label: this.translateService.instant(field.label),
        placeholder: field.placeholder
          ? this.translateService.instant(field.placeholder)
          : field.placeholder,
      })),
    );

    this.inboundNoticeSearchFormat.set(this.buildInboundNoticeSearchFormat());

    this.warehouseResultSearchFormat.set([
      {
        key: 'queryCode',
        label: this.translateService.instant(
          'inbound.table.columns.inbound_result_or_warrant_code',
        ),
        type: 'text',
        placeholder: this.translateService.instant(
          'inbound.placeholders.enter_preceding_document_code',
        ),
      },
      // {
      //   key: 'channelTypeCode',
      //   label: this.translateService.instant(
      //     'inbound.table.columns.sales_channel'
      //   ),
      //   type: 'text',
      //   placeholder: this.translateService.instant(
      //     'inbound.placeholders.enter_channel_type_code'
      //   ),
      // },
      // {
      //   key: 'storeIdList',
      //   label: this.translateService.instant(
      //     'inbound.table.columns.sales_store'
      //   ),
      //   type: 'text',
      //   placeholder: this.translateService.instant(
      //     'inbound.placeholders.enter_store_name'
      //   ),
      // },
      // Temporarily hidden - can be restored later
      // {
      //   key: 'statuses',
      //   label: this.translateService.instant('inbound.table.columns.status'),
      //   type: 'multiselect',
      //   placeholder: this.translateService.instant(
      //     'inbound.placeholders.select_status'
      //   ),
      //   options: [], // Will be populated from API
      // },
      {
        key: 'receivingLogicWarehouseCode',
        label: this.translateService.instant(
          'inbound.table.columns.receiving_logical_warehouse_code',
        ),
        type: 'text',
        placeholder: this.translateService.instant(
          'inbound.placeholders.enter_receiving_logic_warehouse_code',
        ),
      },
      {
        key: 'creationTime',
        label: this.translateService.instant(
          'inbound.table.columns.creation_time',
        ),
        type: 'dateRange',
        range: { startKey: 'beginCreationDate', endKey: 'endCreationDate' },
      },
    ]);

    // Column configurations are now computed signals, no need to set them here
  }

  // Column configuration click handlers
  onColumnConfigClickWarrant(event: Event): void {
    openAdvancedTableColumnSelector(this.advancedTableWarrant, event);
  }

  onColumnConfigClickNotice(event: Event): void {
    openAdvancedTableColumnSelector(this.advancedTableNotice, event);
  }

  onColumnConfigClickResult(event: Event): void {
    openAdvancedTableColumnSelector(this.advancedTableResult, event);
  }

  private addActionIfAllowed(
    actions: string[],
    action: string,
    allowed: boolean,
  ): void {
    if (allowed) {
      actions.push(action);
    }
  }
}
